import { useEffect, useState } from "react";
import { api, formatNOK } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Plus, Pencil, Trash2, Scissors } from "lucide-react";
import { CardGridSkeleton } from "@/components/Skeletons";
import ConfirmDialog from "@/components/ConfirmDialog";
import { toast } from "sonner";
import { PANEL_CLS, PAGE_EYEBROW_CLS, PAGE_H1_CLS, PRIMARY_BTN_CLS } from "@/components/premium";

const empty = { name: "", description: "", duration_min: 30, price_nok: 0, active: true };

export default function Services() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    setLoading(true);
    try { const res = await api.get("/services"); setList(res.data); }
    catch { toast.error("Kunne ikke laste tjenester"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => { setEdit(null); setForm(empty); setOpen(true); };
  const startEdit = (s) => { setEdit(s); setForm({ ...s }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.duration_min) return toast.error("Navn og varighet er påkrevd");
    const payload = {
      name: form.name, description: form.description || "",
      duration_min: Number(form.duration_min), price_nok: Number(form.price_nok), active: !!form.active,
    };
    try {
      if (edit) await api.put(`/services/${edit.id}`, payload);
      else await api.post("/services", payload);
      toast.success("Lagret"); setOpen(false); load();
    } catch { toast.error("Kunne ikke lagre"); }
  };

  const remove = async (id) => {
    try { await api.delete(`/services/${id}`); toast.success("Slettet"); load(); }
    catch { toast.error("Kunne ikke slette"); }
  };

  return (
    <div className="space-y-6" data-testid="services-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <p className={PAGE_EYEBROW_CLS}>Tjenester</p>
          <h1 className={PAGE_H1_CLS}>Tjenestekatalog</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={startCreate} className={PRIMARY_BTN_CLS} data-testid="new-service-btn">
              <Plus className="w-4 h-4" strokeWidth={2.25} /> Ny tjeneste
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-2xl max-w-md">
            <DialogHeader><DialogTitle className="font-heading">{edit ? "Rediger tjeneste" : "Ny tjeneste"}</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div><Label>Navn</Label><Input className="rounded-xl mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="service-name-input" /></div>
              <div><Label>Beskrivelse</Label><Textarea className="rounded-xl mt-1.5" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="service-desc-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Varighet (min)</Label><Input type="number" min="5" className="rounded-xl mt-1.5" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: e.target.value })} data-testid="service-duration-input" /></div>
                <div><Label>Pris (NOK)</Label><Input type="number" min="0" className="rounded-xl mt-1.5" value={form.price_nok} onChange={(e) => setForm({ ...form, price_nok: e.target.value })} data-testid="service-price-input" /></div>
              </div>
              <div className="flex items-center justify-between rounded-xl border border-border p-3">
                <span className="text-sm">Aktiv (synlig for kunder)</span>
                <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="service-active-switch" />
              </div>
              <Button type="submit" className={`w-full ${PRIMARY_BTN_CLS} h-11`} data-testid="service-submit-btn">Lagre</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <CardGridSkeleton count={6} />
      ) : list.length === 0 ? (
        <div className={`${PANEL_CLS} text-center py-16`} data-testid="services-empty">
          <div className="inline-flex w-14 h-14 rounded-2xl bg-emerald-500/15 ring-1 ring-emerald-500/30 items-center justify-center text-emerald-500 mb-3">
            <Scissors className="w-6 h-6" strokeWidth={1.5} />
          </div>
          <p className="font-heading text-xl font-semibold">Ingen tjenester enda</p>
          <p className="text-muted-foreground mt-1">Legg til tjenester slik at kunder kan booke.</p>
          <Button className={`mt-6 ${PRIMARY_BTN_CLS}`} onClick={startCreate}>
            <Plus className="w-4 h-4" strokeWidth={2.25} /> Legg til første tjeneste
          </Button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((s) => (
            <Card key={s.id} className={`${PANEL_CLS} card-hover hover:ring-emerald-500/20`} data-testid="service-card">
              <CardContent className="p-6">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="font-heading text-lg font-semibold truncate">{s.name}</h3>
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{s.description || "—"}</p>
                  </div>
                  {!s.active && <span className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">Inaktiv</span>}
                </div>
                <div className="mt-4 flex items-baseline justify-between">
                  <span className="font-heading text-2xl font-bold">{formatNOK(s.price_nok)}</span>
                  <span className="text-xs text-muted-foreground">{s.duration_min} min</span>
                </div>
                <div className="mt-4 flex gap-2">
                  <Button variant="outline" size="sm" className="rounded-xl flex-1" onClick={() => startEdit(s)} data-testid="edit-service-btn">
                    <Pencil className="w-3.5 h-3.5 mr-1" /> Rediger
                  </Button>
                  <ConfirmDialog
                    title="Slette denne tjenesten?"
                    description={`"${s.name}" vil bli fjernet permanent. Eksisterende bookinger påvirkes ikke.`}
                    confirmLabel="Slett"
                    destructive
                    onConfirm={() => remove(s.id)}
                    testId={`confirm-delete-service-${s.id}`}
                    trigger={(open) => (
                      <Button variant="outline" size="sm" className="rounded-xl" onClick={open} data-testid="delete-service-btn">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
